import React, { createElement, useEffect, useRef, useState } from "react";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
    Button,
    ButtonProps,
    Divider,
    ListItemIcon,
    ListItemText,
    SxProps,
    Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
    LoadingButton,
    LoadingButtonProps,
    LoadingIconButton,
} from "./loadingButton";

export interface IMuiMenuItemProps {
    label?: string | JSX.Element;
    icon?: any;
    onClick?: () => void;
    divider?: boolean;
    disabled?: boolean;
    tooltip?: string;
}

interface IMuiMenuProps {
    label: any;
    size?: IconButtonProps["size"];
    items: IMuiMenuItemProps[];
    keepOpenAfterSelect?: boolean;
    disabled?: boolean;
    sx?: SxProps;
    buttonSx?: SxProps;
    defaultOpen?: boolean;
    noBox?: boolean;
    iconButton?: boolean;
    buttonColor?: ButtonProps["color"];
    loading?: boolean;
}

export default function MuiMenu({
    label,
    size,
    items,
    keepOpenAfterSelect,
    disabled,
    sx,
    buttonSx,
    defaultOpen,
    noBox,
    iconButton = true,
    buttonColor,
    loading,
}: IMuiMenuProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const buttonRef = useRef<HTMLButtonElement>();
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        event.stopPropagation();
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleItemClick = (item: IMuiMenuItemProps) => {
        if (!keepOpenAfterSelect) {
            handleClose();
        }
        if (item.onClick) {
            item.onClick();
        }
    };

    useEffect(() => {
        if (defaultOpen) {
            buttonRef?.current.click();
        }
    }, []);

    const buttonProps = {
        loading,
        ref: buttonRef,
        size: size,
        onClick: handleClick,
        disabled: disabled || loading,
        sx: buttonSx,
        children: label,
        color: buttonColor,
    } as LoadingButtonProps;

    if (loading) {
        buttonProps.loadingIndicatorSize = 20;
    }

    if (!iconButton) {
        buttonProps.variant = "contained";
        buttonProps.endIcon = <KeyboardArrowDownIcon />;
    }

    const iconAndMenu = (
        <>
            {createElement(
                loading
                    ? iconButton
                        ? LoadingIconButton
                        : LoadingButton
                    : iconButton
                        ? IconButton
                        : Button,
                buttonProps
            )}
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {items.filter(Boolean).map((i, idx) => {
                    const item = !i.divider ? (
                        <MenuItem
                            key={`${idx}-${i.label}`}
                            onClick={
                                i.disabled
                                    ? undefined
                                    : () => handleItemClick(i)
                            }
                            disabled={i.disabled}
                            sx={{ pointerEvents: "auto !important" }}
                        >
                            {i.icon ? (
                                <ListItemIcon>{i.icon}</ListItemIcon>
                            ) : null}
                            <ListItemText>{i.label}</ListItemText>
                        </MenuItem>
                    ) : null;

                    return i.divider ? (
                        <Divider key={`div-${idx}`} />
                    ) : !!i.tooltip ? (
                        <Tooltip key={`${idx}-${i.tooltip}`} title={i.tooltip}>
                            {item}
                        </Tooltip>
                    ) : (
                        item
                    );
                })}
            </Menu>
        </>
    );

    return noBox ? iconAndMenu : <Box sx={sx}>{iconAndMenu}</Box>;
}
